import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'antd'
// html2canvas e' nelle dipendenze della radice del monorepo (gia usato da CurriculumForm)
// eslint-disable-next-line import/no-extraneous-dependencies
import html2canvas from 'html2canvas'
import apiClient from 'services/axios'
import { getUrl } from 'services/api'
import { raccogliDiagnostica } from 'services/diagnostica'
import styles from './style.module.scss'

/**
 * Richiesta di assistenza (doc 46): si apre dall'icona Assistenza in alto o da qualunque punto
 * con window.dispatchEvent(new Event('performa-supporto')).
 *
 * Per semplificare il lavoro di chi risponde, l'utente puo' allegare:
 *  - una schermata della pagina, catturata con un clic (la finestra si nasconde un istante);
 *  - fino a 3 immagini sue (anche incollate con Cmd+V);
 *  - le informazioni tecniche (pagina, browser, ultimi errori e chiamate fallite), spuntate
 *    di predefinito e visibili prima dell'invio.
 * API: POST api/support (performa-workflow, SupportoController).
 */

export const EVENTO_SUPPORTO = 'performa-supporto'
export const apriSupporto = () => window.dispatchEvent(new Event(EVENTO_SUPPORTO))

const TEMI = [
  ['problema', 'Qualcosa non funziona'],
  ['domanda', 'Ho una domanda'],
  ['richiesta', 'Mi serve una mano'],
]
const MAX_FILE = 3
const MAX_MB = 5

const Supporto = () => {
  const [aperto, setAperto] = useState(false)
  const [nascosto, setNascosto] = useState(false)
  const [tema, setTema] = useState('problema')
  const [testo, setTesto] = useState('')
  const [schermata, setSchermata] = useState(null)
  const [file, setFile] = useState([])
  const [tecniche, setTecniche] = useState(true)
  const [vediTecniche, setVediTecniche] = useState(false)
  const [diag, setDiag] = useState(null)
  const [stato, setStato] = useState('modulo') // modulo | cattura | invio | fatto | errore
  const [esito, setEsito] = useState(null)
  const input = useRef(null)

  const reset = () => {
    setTema('problema')
    setTesto('')
    setSchermata(null)
    setFile([])
    setTecniche(true)
    setVediTecniche(false)
    setStato('modulo')
    setEsito(null)
  }

  const [inDashboard, setInDashboard] = useState(false)

  useEffect(() => {
    // La schermata della pagina in cui si trova l'utente si prende PRIMA di aprire la finestra,
    // cosi' e' la pagina col problema. Dalla dashboard non serve: si suggerisce l'icona in alto.
    const apri = () => {
      reset()
      setDiag(raccogliDiagnostica())
      const pagina = window.location.hash.split('?')[0].replace(/^#\/?/, '').replace(/\/$/, '')
      const home = pagina === '' || pagina === 'home'
      setInDashboard(home)
      if (home) {
        setAperto(true)
        return
      }
      html2canvas(document.body, { scale: 0.7, logging: false, useCORS: true })
        .then(canvas => setSchermata(canvas.toDataURL('image/png')))
        .catch(() => setSchermata(null))
        .finally(() => setAperto(true))
    }
    window.addEventListener(EVENTO_SUPPORTO, apri)
    return () => window.removeEventListener(EVENTO_SUPPORTO, apri)
  }, [])

  const aggiungiFile = lista => {
    const buoni = Array.from(lista || []).filter(
      f => f.type.indexOf('image/') === 0 && f.size <= MAX_MB * 1024 * 1024,
    )
    setFile(prima => [...prima, ...buoni].slice(0, MAX_FILE))
  }

  const incolla = e => {
    const immagini = Array.from((e.clipboardData && e.clipboardData.files) || [])
    if (immagini.length) aggiungiFile(immagini)
  }

  // Cattura dello schermo vera: il browser chiede quale scheda, finestra o schermo condividere
  // (anche un altro programma, una email, un file Excel). Si prende un solo fotogramma e si chiude.
  const puoCatturareSchermo = !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia)
  const catturaSchermo = () => {
    setStato('cattura')
    setNascosto(true)
    let flusso = null
    navigator.mediaDevices
      .getDisplayMedia({ video: true, audio: false })
      .then(s => {
        flusso = s
        const video = document.createElement('video')
        video.muted = true
        video.srcObject = s
        return video.play().then(() => new Promise(ok => setTimeout(() => ok(video), 400)))
      })
      .then(
        video =>
          new Promise(ok => {
            const canvas = document.createElement('canvas')
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            canvas.getContext('2d').drawImage(video, 0, 0)
            canvas.toBlob(blob => ok(blob), 'image/jpeg', 0.85)
          }),
      )
      .then(blob => {
        if (blob) aggiungiFile([new File([blob], `schermo-${Date.now()}.jpg`, { type: 'image/jpeg' })])
      })
      .catch(() => {
        // l'utente ha annullato la scelta: nessun problema
      })
      .finally(() => {
        if (flusso) flusso.getTracks().forEach(t => t.stop())
        setNascosto(false)
        setStato('modulo')
      })
  }

  const invia = () => {
    if (!testo.trim()) {
      setEsito({ message: 'Scrivi due righe su cosa succede.' })
      setStato('errore')
      return
    }
    setStato('invio')
    const dati = new FormData()
    dati.append('topic', tema)
    dati.append('body', testo)
    dati.append('page', window.location.href.split('?')[0])
    if (tecniche && diag) dati.append('diagnostics', JSON.stringify(diag))
    if (schermata) dati.append('screenshot', schermata)
    file.forEach(f => dati.append('files[]', f))
    apiClient
      .post(getUrl('api/support'), dati)
      .then(res => {
        setEsito(res.data)
        setStato('fatto')
      })
      .catch(err => {
        const r = err && err.response && err.response.data
        setEsito(r || { message: 'Invio non riuscito, riprova tra poco.' })
        setStato('errore')
      })
  }

  const chiudi = () => setAperto(false)
  if (!aperto) return null

  const fatto = stato === 'fatto'
  const footer = fatto ? (
    <button type="button" className={styles.primary} onClick={chiudi}>
      Chiudi
    </button>
  ) : (
    <div className={styles.footer}>
      <button type="button" className={styles.secondary} onClick={chiudi}>
        Annulla
      </button>
      <button type="button" className={styles.primary} onClick={invia} disabled={stato === 'invio'}>
        {stato === 'invio' ? 'Invio…' : 'Invia al supporto'}
      </button>
    </div>
  )

  return (
    <Modal
      visible={!nascosto}
      title="Assistenza Performa"
      onCancel={chiudi}
      footer={footer}
      width={560}
      destroyOnClose={false}
    >
      {fatto ? (
        <div className={styles.box}>
          <span className={styles.ok} aria-hidden="true">
            ✓
          </span>
          <b className={styles.big}>Richiesta #{esito && esito.id} inviata</b>
          <p>
            {esito && esito.inviata
              ? 'Il team di Performa ti risponde via email, alla tua casella.'
              : 'Ambiente di prova: la richiesta è registrata ma nessuna email è partita.'}
          </p>
        </div>
      ) : (
        <div className={styles.box} onPaste={incolla}>
          <p>Raccontaci cosa succede: più dettagli ci dai, prima lo risolviamo.</p>

          <div className={styles.chips} role="radiogroup" aria-label="Tipo di richiesta">
            {TEMI.map(([v, l]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={tema === v}
                className={tema === v ? styles.chipOn : styles.chip}
                onClick={() => setTema(v)}
              >
                {l}
              </button>
            ))}
          </div>

          <label htmlFor="sup-testo" className={styles.label}>
            Cosa succede
          </label>
          <textarea
            id="sup-testo"
            rows={4}
            className={styles.input}
            value={testo}
            placeholder="Es. Quando salvo il candidato compare un errore e i dati non restano."
            onChange={e => setTesto(e.target.value)}
          />

          <span className={styles.label}>Immagini (facoltative)</span>
          <div className={styles.shots}>
            {schermata && (
              <div className={styles.thumb}>
                <img src={schermata} alt="Schermata della pagina da cui hai aperto l'assistenza" />
                <span className={styles.thumbTag}>Questa pagina</span>
                <button type="button" onClick={() => setSchermata(null)} aria-label="Togli la schermata">
                  ×
                </button>
              </div>
            )}
            {puoCatturareSchermo && file.length < MAX_FILE && (
              <button
                type="button"
                className={styles.add}
                onClick={catturaSchermo}
                disabled={stato === 'cattura'}
              >
                <span aria-hidden="true">🖥️</span>
                {stato === 'cattura' ? 'Scegli cosa…' : 'Cattura lo schermo'}
              </button>
            )}
            {file.map((f, i) => (
              <div key={`${f.name}-${f.size}`} className={styles.thumb}>
                <img src={URL.createObjectURL(f)} alt={f.name} />
                <button
                  type="button"
                  onClick={() => setFile(file.filter((_, j) => j !== i))}
                  aria-label={`Togli ${f.name}`}
                >
                  ×
                </button>
              </div>
            ))}
            {file.length < MAX_FILE && (
              <button type="button" className={styles.add} onClick={() => input.current && input.current.click()}>
                <span aria-hidden="true">🖼️</span>
                Aggiungi immagine
              </button>
            )}
            <input
              ref={input}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={e => {
                aggiungiFile(e.target.files)
                e.target.value = ''
              }}
            />
          </div>
          {inDashboard ? (
            <p className={styles.tip}>
              <b>Il problema è in un&apos;altra pagina?</b> Aprila e clicca l&apos;icona <b>Assistenza</b> in alto:
              la schermata di quella pagina si allega da sola.
            </p>
          ) : (
            schermata && (
              <small className={styles.hint}>
                Abbiamo allegato la pagina in cui eri. Se non serve, toglila con la ×.
              </small>
            )
          )}
          <small className={styles.hint}>
            &quot;Cattura lo schermo&quot; ti fa scegliere una scheda, una finestra o lo schermo intero. Puoi
            anche incollare un&apos;immagine con Cmd+V. Massimo 3, fino a 5 MB.
          </small>

          <div className={styles.tech}>
            <label htmlFor="sup-tech" className={styles.check}>
              <input
                id="sup-tech"
                type="checkbox"
                checked={tecniche}
                onChange={e => setTecniche(e.target.checked)}
              />
              Allega informazioni tecniche
            </label>
            <button type="button" className={styles.link} onClick={() => setVediTecniche(!vediTecniche)}>
              {vediTecniche ? 'Nascondi' : 'Cosa contengono?'}
            </button>
          </div>
          {vediTecniche && diag && (
            <div className={styles.techBox}>
              <p>
                Pagina, browser, dimensioni dello schermo, ultime pagine visitate,{' '}
                {diag.chiamate_fallite.length} chiamate non riuscite e {diag.errori.length} errori
                registrati in questa sessione. Nessun dato di candidati.
              </p>
              <pre>{JSON.stringify(diag, null, 2)}</pre>
            </div>
          )}

          {stato === 'errore' && esito && <p className={styles.err}>{esito.message}</p>}
        </div>
      )}
    </Modal>
  )
}

export default Supporto
